import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteAdmin, getAdminList, saveAdmin, sendEmail } from "../../api";
import { setLoading } from "../../redux/loadingSlice";
import { setNotification } from "../../redux/notificationSlice";
import { AddRecord, DeleteRecord, EditRecord, Pagination } from "../core";
import { ConfirmationModal } from "../modals/ConfirmationModal";
import { UpdateModal } from "../modals/UpdateModal";

export const AdminList = () => {
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminData, setAdminData] = useState([]);
  const [deleteAdminModal, setDeleteAdminModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [editAdmin, setEditAdmin] = useState({
    id: "",
    name: "",
    isid: "",
    role: "",
    lastUpdated: "",
    updatedBy: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const totalPages = Math.ceil(adminData.length / itemsPerPage);
  const currentItems = adminData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const userInfo = useSelector((state) => state.user?.profile);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setLoading(false));
    const fetchAdminData = async () => {
      const req = { operation: "fetchAdmin" };
      try {
        const adminResp = await getAdminList(req);
        if (adminResp.responseCode === 200) {
          setAdminData(adminResp.responseData);
          dispatch(setLoading(true));
        }
      } catch (error) {
        console.error(error);
        dispatch(
          setNotification({
            type: "warning",
            show: true,
            message: "There are some issue fetching admin. Please try again",
          })
        );
      }
    };
    fetchAdminData();
  }, [setAdminData, dispatch]);

  const openAdminModal = (
    data = {
      id: null,
      name: "",
      isid: "",
      role: "",
      lastUpdated: "",
      updatedBy: "",
    }
  ) => {
    setEditAdmin(data);
    setShowAdminModal(true);
  };

  const handleAdminSave = async (data) => {
    let updatedData;
    data.createdBy = userInfo.isid;
    data.updatedBy = userInfo.isid;
    data.role = data.role === "Super Admin" ? "Super_Admin" : data.role;
    if (editAdmin.id) {
      updatedData = {
        operation: "updateAdmin",
        request: { id: editAdmin.id, ...data },
      };
    } else {
      updatedData = { operation: "createAdmin", request: { ...data } };
    }
    const resp = await saveAdmin(updatedData);
    if (resp?.responseCode === 200) {
      if (updatedData.operation === "updateAdmin") {
        const finalUpdatedData = {
          ...updatedData.request,
          updatedOn: resp.responseData.updatedOn,
        };
        setAdminData(
          adminData.map((item) =>
            item.id === updatedData.request.id ? finalUpdatedData : item
          )
        );
        const userIsid = data.isId.toLowerCase();
        const updateEmailSubject =
          "[MSD GCTO Service Provider Portal] Admin Updated";
        const emailBody = `<div className="bg-gray-100 p-8 font-sans">
        <div className="max-w-xl mx-auto bg-white rounded-lg shadow-md p-8">
          <p className="mb-4">Dear ${data.isId},</p>
          <p className="mb-4">
            Your access from the GCTO Service Provider Portal is now updated. You can access the portal here: 
            <a href="${process.env.REACT_APP_BASE_URL}" class="text-blue-600 dark:text-blue-500 hover:underline">GCTO Service Provider</a>
          </p>
          <p className="mb-4">
            Thanks!</br>
            MSD GCTO Service Portal
          </p>
        </div>
      </div>`;
        sendEmail(updateEmailSubject, emailBody, [`${userIsid}@merck.com`]);
        dispatch(
          setNotification({
            type: "success",
            show: true,
            message: "Record updated successfully",
          })
        );
      } else {
        data.updatedOn = resp.responseData.updatedOn;
        data.id = resp.responseData.id;
        setAdminData([...adminData, data]);
        const userIsid = data.isId.toLowerCase();
        const createAdminSubject =
          "[MSD GCTO Service Provider Portal] Admin Created";
        const emailBody = `<div className="bg-gray-100 p-8 font-sans">
          <div className="max-w-xl mx-auto bg-white rounded-lg shadow-md p-8">
            <p className="mb-4">Dear ${data.isId},</p>
              <p className="mb-4">
                You have been added to the GCTO Service Provider Portal. You can access the portal here: 
                <a href="${process.env.REACT_APP_BASE_URL}" class="text-blue-600 dark:text-blue-500 hover:underline">GCTO Service Provider</a>
              </p>
              <p className="mb-4">
                Thanks!</br>
                MSD GCTO Service Portal
              </p>
          </div>
        </div>`;
        sendEmail(createAdminSubject, emailBody, [`${userIsid}@merck.com`]);
        dispatch(
          setNotification({
            type: "success",
            show: true,
            message: "Record created successfully",
          })
        );
      }
    } else if (resp?.responseCode === 400) {
      dispatch(
        setNotification({
          type: "warning",
          show: true,
          message: "Record already exist.",
        })
      );
    } else {
      dispatch(
        setNotification({
          type: "error",
          show: true,
          message: "Error! Something went wrong. Please try again",
        })
      );
    }
  };

  const openAdminDeleteModal = (data) => {
    setDeleteAdminModal(true);
    setRowToDelete(data);
  };

  const deleteAdminRow = async () => {
    const dataToDelete = {
      operation: "removeAdmin",
      request: { id: rowToDelete.id },
    };
    const resp = await deleteAdmin(dataToDelete);
    if (resp.responseCode === 200) {
      setAdminData(adminData.filter((item) => item.id !== rowToDelete.id));
      const userIsid = rowToDelete.isId.toLowerCase();
      const deleteAdminSubject =
        "[MSD GCTO Service Provider Portal] Admin Deleted";
      const emailBody = `<div className="bg-gray-100 p-8 font-sans">
          <div className="max-w-xl mx-auto bg-white rounded-lg shadow-md p-8">
            <p className="mb-4">Dear ${rowToDelete.isId},</p>
            <p className="mb-4">
              Your access from the GCTO Service Provider Portal is now removed.
            </p>
            <p className="mb-4">
              Thanks!</br>
              MSD GCTO Service Portal
            </p>
          </div>
        </div>`;
      sendEmail(deleteAdminSubject, emailBody, [`${userIsid}@merck.com`]);
      setDeleteAdminModal(false);
      setRowToDelete(null);
      dispatch(
        setNotification({
          type: "success",
          show: true,
          message:
            resp.errorMessage === "Ok" ? resp.responseData : resp.errorMessage,
        })
      );
    }
  };

  const closeAdminEditModal = () => {
    setShowAdminModal(false);
  };

  return (
    <>
      <button
        className="bg-sky-800 text-white px-2 py-px float-right mb-2"
        onClick={() => openAdminModal()}
      >
        <AddRecord />
        Add New
      </button>
      <table className="table-auto border-collapse border border-slate-400 min-w-full">
        <thead className="bg-gray-300 text-left">
          <tr className="border border-slate-300">
            <th className="p-2">Name</th>
            <th className="p-2">ISID</th>
            <th className="p-2">Role</th>
            <th className="p-2">Last Updated</th>
            <th className="p-2">Last Updated by</th>
            {userInfo && userInfo.role !== "Read" ? (
              <th className="p-2"></th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {currentItems?.map((row) => (
            <tr
              key={`${row.isId}-${row.id}`}
              className="border border-slate-300"
            >
              <td className="px-4 py-2">{row.name}</td>
              <td className="px-4 py-2">{row.isId}</td>
              <td className="px-4 py-2">
                {row.role === "Super_Admin" ? "Super Admin" : row.role}
              </td>
              <td className="px-4 py-2">
                {row.updatedOn
                  ?.replace(/^"(.*)"$/, "$1")
                  .replace(/\.\d{6}$/, "")}
              </td>
              <td className="px-4 py-2">{row.updatedBy}</td>
              {userInfo && userInfo.role !== "Read" ? (
                <td className="p-2 text-right">
                  <button
                    className={`text-white px-2 py-px ${
                      userInfo.role === "Admin" && row.role === "Super_Admin"
                        ? "cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    value={row.id}
                    onClick={() => openAdminModal(row)}
                    disabled={
                      userInfo.role === "Admin" && row.role === "Super_Admin"
                    }
                    data-testid="edit-admin"
                  >
                    <EditRecord />
                  </button>
                  <button
                    className={`text-white px-2 py-px ${
                      userInfo.role === "Admin" && row.role === "Super_Admin"
                        ? "cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    value={row.id}
                    onClick={() => openAdminDeleteModal(row)}
                    data-testid="delete-admin"
                    disabled={
                      userInfo.role === "Admin" && row.role === "Super_Admin"
                    }
                  >
                    <DeleteRecord />
                  </button>
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      {adminData.length > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
      <UpdateModal
        isOpen={showAdminModal}
        header={"Add/Edit Admin"}
        fields={[
          { name: "name", label: "Name" },
          { name: "isId", label: "ISID" },
          { name: "role", label: "Role" },
        ]}
        initialData={editAdmin}
        onClose={closeAdminEditModal}
        onSubmit={handleAdminSave}
      />

      <ConfirmationModal
        isOpen={deleteAdminModal}
        onClose={() => setDeleteAdminModal(false)}
        onDelete={() => deleteAdminRow()}
        header="Confirm delete Admin"
        message="Are you sure you want to delete? This action cannot be undone."
      />
    </>
  );
};
